import logger from '@/utils/client-logger';

/**
 * 兼容旧数据的自定义阻塞时间采集。
 *
 * 该实现从 FCP 开始累计 Long Tasks 超过 50ms 的部分，直到页面首次隐藏；它没有
 * Lighthouse TBT 所要求的 TTI 截止点，因此不属于标准 TBT，不能与 Lighthouse 结果对比。
 */

export interface LongTaskLike {
  startTime: number;
  duration: number;
}

export interface TbtMetric {
  id: string;
  value: number;
  delta: number;
}

export interface TbtState {
  metricId: string;
  fcpTime: number | null;
  entries: LongTaskLike[];
  reported: boolean;
}

export interface TbtMonitorDocument {
  visibilityState: string;
  addEventListener: Document['addEventListener'];
  removeEventListener: Document['removeEventListener'];
}

export interface TbtMonitorHandle {
  setFcpTime: (fcpTime: number) => void;
  stop: () => void;
}

export type TbtPerformanceObserverConstructor =
  | (new (
      callback: (list: { getEntries: () => Array<PerformanceEntry | LongTaskLike> }) => void,
    ) => {
      observe: (options: { type: string; buffered: boolean }) => void;
      disconnect: () => void;
    })
  | undefined;

export function createTbtState(metricId = `tbt-${Date.now()}`): TbtState {
  return {
    metricId,
    fcpTime: null,
    entries: [],
    reported: false,
  };
}

export function setTbtFcpTime(state: TbtState, fcpTime: number): void {
  if (state.fcpTime === null) {
    state.fcpTime = fcpTime;
  }
}

export function addLongTaskEntries(state: TbtState, entries: LongTaskLike[]): void {
  state.entries.push(
    ...entries.map((entry) => ({
      startTime: entry.startTime,
      duration: entry.duration,
    })),
  );
}

export function calculateTbt(entries: LongTaskLike[], fcpTime: number): number {
  return Math.round(
    entries.reduce((total, entry) => {
      if (entry.startTime < fcpTime || entry.duration <= 50) {
        return total;
      }

      return total + entry.duration - 50;
    }, 0),
  );
}

export function createTbtMetric(state: TbtState): TbtMetric | null {
  if (state.reported || state.fcpTime === null) {
    return null;
  }

  const value = calculateTbt(state.entries, state.fcpTime);
  state.reported = true;

  return {
    id: state.metricId,
    value,
    delta: value,
  };
}

export function startTbtMonitor(options: {
  document?: TbtMonitorDocument;
  PerformanceObserver?: TbtPerformanceObserverConstructor;
  metricId?: string;
  report: (metric: TbtMetric) => void;
}): TbtMonitorHandle {
  const documentLike = options.document ?? (typeof document !== 'undefined' ? document : undefined);
  const Observer = Object.hasOwn(options, 'PerformanceObserver')
    ? options.PerformanceObserver
    : typeof PerformanceObserver !== 'undefined'
      ? PerformanceObserver
      : undefined;
  const state = createTbtState(options.metricId);

  if (!documentLike || !Observer) {
    return {
      setFcpTime: (fcpTime) => setTbtFcpTime(state, fcpTime),
      stop: () => undefined,
    };
  }

  let observer: {
    observe: (options: { type: string; buffered: boolean }) => void;
    disconnect: () => void;
  } | null = null;
  let stopped = false;

  const cleanup = () => {
    if (stopped) return;
    stopped = true;
    observer?.disconnect();
    documentLike.removeEventListener('visibilitychange', handleVisibilityChange);
  };

  const reportOnce = () => {
    try {
      const metric = createTbtMetric(state);
      if (!metric) return;

      try {
        options.report(metric);
      } catch (error) {
        logger.error('FrontendPerformance TBT report failed:', error);
      }
    } finally {
      cleanup();
    }
  };

  function handleVisibilityChange() {
    if (documentLike.visibilityState === 'hidden') {
      reportOnce();
    }
  }

  try {
    observer = new Observer((list) => {
      addLongTaskEntries(
        state,
        list.getEntries().map((entry) => ({
          startTime: entry.startTime,
          duration: entry.duration,
        })),
      );
    });
    observer.observe({ type: 'longtask', buffered: true });
    documentLike.addEventListener('visibilitychange', handleVisibilityChange);
  } catch (error) {
    logger.warn('FrontendPerformance TBT observer unavailable:', error);
    cleanup();
  }

  return {
    setFcpTime: (fcpTime) => setTbtFcpTime(state, fcpTime),
    stop: cleanup,
  };
}
